/**
 * Turning the camera round the player, Ragnarok Online style (no drawing here, so it is tested in
 * Node): the angle the view is turned to, eased toward where the mouse, the keys or the stick want
 * it, and how a right-button press tells a guard from a drag that turns the camera.
 *
 * Angles are in quarter turns (1 = 90°) and unwrapped: dragging round twice is 8, not 0. The
 * renderer (render/turnView.ts) splits the angle into the quarter turn the art is painted for and
 * the turn past it (IsoCamera.setAngle).
 */

/** A right-button drag turns the camera once it has gone this far sideways (CSS pixels). */
export const TURN_DRAG_PX = 6;
/** Quarter turns per CSS pixel dragged: about 90° for 280 px. */
export const TURN_PER_PX = 1 / 280;
/** How fast the angle eases to its goal (per second, exponential). */
const TURN_EASE = 14;
/** How fast the angle follows a drag (per second, exponential): about 50 ms behind the mouse. */
const DRAG_EASE = 20;
/** Turning speed while a key or the stick is held (quarter turns a second). */
const TURN_HOLD_RATE = 1.6;
/** How fast a held turn gets up to speed (per second, exponential): about 0.2 s to full speed. */
const TURN_HOLD_EASE = 12;

/**
 * The right mouse button: pressed it guards at once, as it always has; dragged sideways past
 * `TURN_DRAG_PX` (more sideways than up or down) it becomes a camera turn; the guard stays up until the button
 * comes up (user 10-11: turning the camera dropped the guard).
 */
export class RightDrag {
  phase: 'idle' | 'guard' | 'turn' = 'idle';
  /** The guard raised by the press is held through a turn (input.ts reads it). */
  readonly keepsGuard = true;
  private x0 = 0;
  private y0 = 0;
  private lastX = 0;
  private lastY = 0;

  press(x: number, y: number): void {
    this.phase = 'guard';
    this.x0 = this.lastX = x;
    this.y0 = y;
  }

  /**
   * The pointer moved with the button held. `started` is true on the move that turns a guard into
   * a turn; `dx` is how far to turn by (pixels): only the travel past the point where it became a
   * turn, so the view starts from where it was rather than jumping by the threshold.
   */
  move(x: number, y: number): { started: boolean; dx: number; dy: number } {
    if (this.phase === 'guard') {
      // C0-ROTATE: sideways turns the camera, up or down tilts it (both once it is a drag).
      if (Math.hypot(x - this.x0, y - this.y0) > TURN_DRAG_PX) {
        this.phase = 'turn';
        this.lastX = x;
        this.lastY = y;
        return { started: true, dx: 0, dy: 0 };
      }
      return { started: false, dx: 0, dy: 0 };
    }
    if (this.phase !== 'turn') return { started: false, dx: 0, dy: 0 };
    const dx = x - this.lastX;
    const dy = y - this.lastY;
    this.lastX = x;
    this.lastY = y;
    return { started: false, dx, dy };
  }

  /** The button came up: what the press was (a guard, a turn, or nothing). */
  release(): 'idle' | 'guard' | 'turn' {
    const was = this.phase;
    this.phase = 'idle';
    return was;
  }
}

/**
 * The camera's angle and where it is heading. Snapped (the default), it comes to rest on the
 * quarter turns the world is painted for; free (`?camera=free`, a prototype), wherever it is let go.
 */
export class CameraTurn {
  /** The angle shown (quarter turns, unwrapped). */
  angle = 0;
  /** Where the angle is easing to. */
  goal = 0;
  private dragging = false;
  /** Held turning (keys or stick): −1..1, 0 for none. */
  private held = 0;
  /** The held turn's speed now (quarter turns a second), easing toward `held × TURN_HOLD_RATE`. */
  private speed = 0;
  /** Called once the angle has come to rest after moving (to save it). */
  onSettle: ((angle: number) => void) | null = null;
  private moving = false;

  constructor(
    /** Rest anywhere (true) or on the nearest quarter turn (false). */
    readonly free = false,
    /** Rest angles per quarter turn when snapped: 1 = every 90°, 2 = every 45°. */
    readonly stops = 1,
  ) {}

  /** The nearest angle the camera may rest at. */
  rest(a: number): number {
    if (this.free) return a;
    return Math.round(a * this.stops) / this.stops;
  }

  /** Jumps to an angle at once (loading the saved one, a cutscene's). */
  set(a: number): void {
    this.angle = this.goal = a;
    this.held = this.speed = 0;
    this.dragging = false;
  }

  /** The camera follow (cameraFollow.ts) swings the view on by `dq` quarter turns, unless the player is turning it. */
  follow(dq: number): void {
    if (this.dragging || this.held) return;
    this.angle += dq;
    this.goal = this.angle;
  }

  /** A right-button drag began. */
  grab(): void {
    this.dragging = true;
    this.speed = 0;
    this.goal = this.angle;
  }

  /**
   * The drag moved `px` CSS pixels sideways: the goal follows it, and the angle follows the goal
   * each frame (`DRAG_EASE`), so uneven mouse events turn the view at an even speed.
   */
  drag(px: number): void {
    if (!this.dragging) this.grab();
    // Grab the ground in front of the player and pull: dragging right swings it right.
    this.goal -= px * TURN_PER_PX;
  }

  /** The drag let go: ease on to where it was heading (the nearest rest angle when snapped). */
  release(): void {
    if (!this.dragging) return;
    this.dragging = false;
    this.goal = this.rest(this.goal);
  }

  /** A key tap: one rest angle on (+1 clockwise, −1 the other way), from wherever it is heading. */
  nudge(dir: number): void {
    const step = this.free ? 0.5 : 1 / this.stops;
    this.goal = this.rest(this.goal) + Math.sign(dir) * step;
  }

  /**
   * Held turning (a key or the stick, −1..1): the turn eases up to speed. 0 lets go: it coasts on
   * and eases to a stop (free) or to the nearest rest angle, with no jolt in speed.
   */
  hold(dir: number): void {
    if (dir === this.held) return;
    if (!dir && this.held) this.goal = this.rest(this.angle + this.speed / TURN_EASE);
    this.held = dir;
  }

  get busy(): boolean {
    return this.dragging || this.held !== 0 || this.angle !== this.goal;
  }

  /** Moves the angle on by `dt` seconds; true if it changed. */
  step(dt: number): boolean {
    const from = this.angle;
    if (this.held && !this.dragging) {
      const want = this.held * TURN_HOLD_RATE;
      this.speed += (want - this.speed) * (1 - Math.exp(-TURN_HOLD_EASE * dt));
      this.angle += this.speed * dt;
      this.goal = this.angle;
    } else if (this.dragging) {
      // Dragging: the angle follows the drag's goal, smoothed over a few frames, by frame time.
      const next = this.angle + (this.goal - this.angle) * (1 - Math.exp(-DRAG_EASE * dt));
      this.angle = Math.abs(this.goal - next) < 1e-4 ? this.goal : next;
    } else if (this.angle !== this.goal) {
      // Let go of a hold, the ease from here starts at the speed it had (gap × TURN_EASE = speed).
      this.speed = 0;
      const gap = this.goal - this.angle;
      const next = this.angle + gap * (1 - Math.exp(-TURN_EASE * dt));
      this.angle = Math.abs(this.goal - next) < 1e-3 ? this.goal : next;
    }
    const changed = this.angle !== from;
    if (changed) this.moving = true;
    else if (this.moving && !this.busy) {
      this.moving = false;
      this.onSettle?.(this.angle);
    }
    return changed;
  }
}

/** Pitch: degrees per CSS pixel dragged up or down (about 30° for 150 px). */
export const PITCH_PER_PX = 0.2;
/** Pitch speed while PageUp / PageDown is held (degrees a second). */
const PITCH_HOLD_RATE = 45;
/** How fast the pitch eases to its goal (per second, exponential). */
const PITCH_EASE = 12;
/**
 * The pitch's range (lane CAMERA-PITCH, render/pitchView.ts): 22° (the user's floor, 10-11; the renderer handles 0°
 * ground level with a horizon slice) to 45° (the user's cap, 10-11; the renderer handles up to 90°, kept behind this
 * clamp); 30° is the painted view and the start.
 */
export const PITCH_MIN = 22;
export const PITCH_MAX = 45;
export const PITCH_PAINTED = 30;

/**
 * C0-ROTATE: the camera's elevation (degrees), eased toward where the drag or the keys want it.
 * 30 is the painted view; lower is flatter, higher looks further down.
 */
export class CameraPitch {
  pitch = PITCH_PAINTED;
  goal = PITCH_PAINTED;
  private held = 0;

  private clamp(p: number): number {
    return Math.max(PITCH_MIN, Math.min(PITCH_MAX, p));
  }

  /** At once (a screenshot, a load). */
  set(p: number): void {
    this.pitch = this.goal = this.clamp(p);
    this.held = 0;
  }

  /** A right-drag moved `px` CSS pixels down (negative: up). Dragging up flattens the view. */
  drag(px: number): void {
    this.goal = this.clamp(this.goal + px * PITCH_PER_PX);
  }

  /** PageUp (+1, looking further down) / PageDown (−1, flatter) held, or let go (0). */
  hold(dir: number): void {
    this.held = dir;
  }

  /** Moves the pitch on by `dt` seconds; true if it changed. */
  step(dt: number): boolean {
    const from = this.pitch;
    if (this.held) this.goal = this.clamp(this.goal + this.held * PITCH_HOLD_RATE * dt);
    if (this.pitch !== this.goal) {
      const next = this.pitch + (this.goal - this.pitch) * (1 - Math.exp(-PITCH_EASE * dt));
      this.pitch = Math.abs(this.goal - next) < 0.01 ? this.goal : next;
    }
    return this.pitch !== from;
  }
}
